/**
 * Resource-timing helpers shared by the AI perf tool and the live performance
 * event strip — read the browser's PerformanceResourceTiming entries and keep the
 * sensitive-URL filtering in ONE place.
 */

/**
 * Drop URLs that would leak sensitive endpoints (licensing / analytics / auth
 * tokens). Returns the url when safe to surface, null when it must be excluded.
 * Pure.
 */
export function filterSensitiveResourceUrls(url: string): string | null {
    const sensitivePatterns = [
        /needle\.tools\/licensing\/check/,
        /analytics\.needle\.tools\//,
        /api\.needle\.tools\/auth\/token/,
        /https:\/\/proxy____staging\.needle\.run/,
        /https:\/\/proxy\.needle\.run/,
        /https:\/\/needle\.tools\/api\//, // ignore needle api calls (e.g. telemetry)
    ];
    for (const pattern of sensitivePatterns) {
        if (pattern.test(url)) return null;
    }
    return url;
}

/** Editorial media loaded by Inspector UI, not by the inspected experience. */
export function isInspectorOwnedMediaUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        const host = parsed.hostname.toLowerCase();
        return (host === "cloud.needle.tools" && parsed.pathname.startsWith("/-/media/"))
            || host === "needle.click";
    }
    catch {
        return false;
    }
}

/** Bytes a resource cost on the wire (transfer), falling back to encoded/decoded. Pure. */
export function resourceEntrySize(entry: Pick<PerformanceResourceTiming, "transferSize" | "encodedBodySize" | "decodedBodySize">): number {
    return entry.transferSize || entry.encodedBodySize || entry.decodedBodySize || 0;
}

/** Short display name for a resource URL — the file name, query/hash stripped. Pure. */
export function resourceDisplayName(url: string): string {
    try {
        const u = new URL(url, "http://_"); // base so relative urls parse
        const name = u.pathname.substring(u.pathname.lastIndexOf("/") + 1);
        return name || u.hostname || url;
    } catch {
        const q = url.split(/[?#]/)[0];
        return q.substring(q.lastIndexOf("/") + 1) || url;
    }
}

export interface ResourceTransferEntry {
    readonly url: string;
    readonly bytes: number;
    readonly type?: string;
}

export interface ResourceTransferSummary {
    readonly largestFiles: readonly Required<ResourceTransferEntry>[];
    readonly largestTypes: readonly { type: string; bytes: number }[];
}

/** Coarse network category for a MIME type or URL. MIME wins when available. */
export function resourceType(url: string, contentType = ""): string {
    const mime = contentType.toLowerCase().split(";", 1)[0].trim();
    if (mime.includes("javascript") || mime === "text/ecmascript") return "JS";
    if (mime === "text/css") return "CSS";
    if (mime.includes("gltf") || mime === "model/gltf-binary") return "GLTF";
    if (mime.includes("ktx")) return "KTX";
    if (mime.includes("wasm")) return "WASM";
    if (mime.startsWith("image/")) return "Image";
    if (mime.startsWith("video/")) return "Video";
    if (mime.startsWith("audio/")) return "Audio";
    if (mime.startsWith("font/") || mime.includes("font")) return "Font";
    if (mime.includes("json")) return "JSON";

    const path = url.split(/[?#]/, 1)[0].toLowerCase();
    const extension = path.match(/\.([a-z0-9]+)$/)?.[1] ?? "";
    if (["js", "mjs", "cjs"].includes(extension)) return "JS";
    if (extension === "css") return "CSS";
    if (["gltf", "glb"].includes(extension)) return "GLTF";
    if (["ktx", "ktx2"].includes(extension)) return "KTX";
    if (extension === "wasm") return "WASM";
    if (["png", "jpg", "jpeg", "webp", "avif", "gif", "svg", "ico"].includes(extension)) return "Image";
    if (["mp4", "webm", "mov"].includes(extension)) return "Video";
    if (["mp3", "ogg", "wav", "m4a"].includes(extension)) return "Audio";
    if (["woff", "woff2", "ttf", "otf"].includes(extension)) return "Font";
    if (extension === "json") return "JSON";
    return "Other";
}

export function summarizeResourceTransfers(entries: readonly ResourceTransferEntry[], limit = 5): ResourceTransferSummary {
    const normalized = entries
        .filter(entry => Number.isFinite(entry.bytes) && entry.bytes > 0)
        .map(entry => ({ ...entry, type: entry.type || resourceType(entry.url) }));
    const totals = new Map<string, number>();
    for (const entry of normalized) totals.set(entry.type, (totals.get(entry.type) ?? 0) + entry.bytes);
    return {
        largestFiles: [...normalized].sort((a, b) => b.bytes - a.bytes).slice(0, limit),
        largestTypes: [...totals].map(([type, bytes]) => ({ type, bytes })).sort((a, b) => b.bytes - a.bytes).slice(0, limit),
    };
}

/** All resource-timing entries with sensitive URLs excluded (best-effort; [] when unavailable). */
export function getResourceEntries(): PerformanceResourceTiming[] {
    if (typeof performance === "undefined" || !performance.getEntriesByType) return [];
    return (performance.getEntriesByType("resource") as PerformanceResourceTiming[])
        .filter((r) => !isInspectorOwnedMediaUrl(r.name) && filterSensitiveResourceUrls(r.name) !== null);
}
